import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, Sparkles, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function StatusUpdateSheet({ open, onOpenChange, onDone }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setText(""); setResult(null); };

  const run = async () => {
    if (text.trim().length < 200) {
      toast.error("Bitte komplette Statustabelle einfügen (min. 200 Zeichen).");
      return;
    }
    setRunning(true);
    try {
      const r = await api.syncStatus(text.trim());
      setResult(r);
      const created = r.created?.length || 0;
      const updated = r.updated?.length || 0;
      const errors = r.errors?.length || 0;
      toast.success(`${created} neu, ${updated} aktualisiert${errors ? `, ${errors} Fehler` : ""}.`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl bg-background border-l border-border text-foreground overflow-y-auto"
        data-testid="status-update-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <RefreshCw size={20} className="text-emerald-400" /> Status Update
          </SheetTitle>
          <SheetDescription className="text-zinc-500">
            Friedrich-Statustabelle einfügen. Vorhandene Setups werden per ISIN/WKN/Symbol/Name
            abgeglichen und mit den neuen Zonen aktualisiert. Neue Assets werden automatisch angelegt.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-muted-foreground text-xs uppercase tracking-wider">Statustabelle (kompletter E-Mail-Inhalt)</Label>
            <Textarea
              data-testid="input-status-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Friedrich Report Statustabelle... [füge die komplette E-Mail mit allen Assets ein]"
              rows={14}
              className="bg-muted border-border text-xs mt-1 font-mono-num"
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-muted-foreground">
                Tipp: einfach die ganze E-Mail per Strg+A → Strg+C → hier einfügen.
              </p>
              <p className="text-[10px] text-muted-foreground font-mono-num">{text.length} Zeichen</p>
            </div>
          </div>

          <Button
            onClick={run}
            disabled={running}
            data-testid="btn-run-sync"
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm font-display font-bold uppercase tracking-wider h-11"
          >
            {running ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Analysiere & synchronisiere...</>
            ) : (
              <><Sparkles size={14} className="mr-2" /> Synchronisieren</>
            )}
          </Button>

          {result && (
            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="font-display font-bold text-sm uppercase tracking-wider text-foreground">
                Ergebnis · {result.parsed} Assets erkannt
              </h3>

              <div className="grid grid-cols-3 gap-2">
                <ResultStat label="Neu" value={result.created?.length || 0} accent="emerald" />
                <ResultStat label="Aktualisiert" value={result.updated?.length || 0} accent="amber" />
                <ResultStat label="Fehler" value={result.errors?.length || 0} accent="rose" />
              </div>

              {result.created?.length > 0 && (
                <ResultList title="Neu angelegt" icon={Plus} items={result.created} accent="text-emerald-400" />
              )}
              {result.updated?.length > 0 && (
                <ResultList title="Aktualisiert" icon={RefreshCw} items={result.updated} accent="text-amber-400" />
              )}
              {result.errors?.length > 0 && (
                <div>
                  <div className="font-display font-bold text-xs uppercase tracking-wider text-rose-400 mb-1 flex items-center gap-1">
                    <AlertCircle size={12} /> Fehler
                  </div>
                  <div className="border border-border bg-background divide-y divide-border">
                    {result.errors.map((e, i) => (
                      <div key={i} className="px-3 py-2 text-xs">
                        <span className="text-foreground">{e.asset}</span>
                        <span className="text-muted-foreground"> · </span>
                        <span className="text-rose-400">{e.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={() => { onOpenChange(false); reset(); }}
                variant="outline"
                className="w-full border-border bg-transparent hover:bg-muted"
                data-testid="btn-close-result"
              >
                Schließen
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ResultStat({ label, value, accent }) {
  const map = { emerald: "text-emerald-400", amber: "text-amber-400", rose: "text-rose-400" };
  return (
    <div className="border border-border bg-background p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display">{label}</div>
      <div className={`mt-1 font-mono-num font-bold text-xl ${map[accent] || "text-foreground"}`}>{value}</div>
    </div>
  );
}

function ResultList({ title, icon: Icon, items, accent }) {
  return (
    <div>
      <div className={`font-display font-bold text-xs uppercase tracking-wider mb-1 flex items-center gap-1 ${accent}`}>
        <Icon size={12} /> {title} ({items.length})
      </div>
      <div className="border border-border bg-background divide-y divide-border max-h-48 overflow-y-auto">
        {items.map((it, i) => (
          <div key={i} className="px-3 py-2 text-xs flex items-center justify-between">
            <span className="text-foreground truncate">{it.asset_name}</span>
            {it.symbol && <span className="text-muted-foreground font-mono-num text-[10px] shrink-0">{it.symbol}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
